/**
 * Cart urgency countdown.
 *
 * Counts down "MM:SS" from a per-session deadline persisted in sessionStorage,
 * so the timer keeps ticking across drawer re-renders (each cart add re-renders
 * the header section) instead of resetting. When it reaches zero it starts a
 * fresh window.
 */
import { ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'sstCartReservationDeadline';

/**
 * Clears the reservation window whenever the cart empties, so the next add
 * starts a full countdown instead of resuming a partly-spent one.
 *
 * Bound at module scope rather than on the element: emptying the cart removes
 * the drawer body (and this element) before the cart:update event fires, so an
 * element-bound listener would already be detached by then.
 * @param {CartUpdateEvent} event
 */
const handleCartUpdate = (event) => {
  const count = event.detail.resource?.item_count ?? event.detail.data?.itemCount;
  if (count !== 0) return;
  sessionStorage.removeItem(STORAGE_KEY);
};

document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);

class SstCartTimer extends HTMLElement {
  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    this.output = this.querySelector('[data-cart-timer]');
    this.durationMs = (Number(this.dataset.minutes) || 10) * 60 * 1000;
    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    if (this.#intervalId) window.clearInterval(this.#intervalId);
  }

  #deadline() {
    let deadline = Number(sessionStorage.getItem(STORAGE_KEY));
    if (!deadline || deadline <= Date.now()) {
      deadline = Date.now() + this.durationMs;
      sessionStorage.setItem(STORAGE_KEY, String(deadline));
    }
    return deadline;
  }

  #tick() {
    if (!this.output) return;
    const remaining = Math.max(0, this.#deadline() - Date.now());
    const totalSeconds = Math.round(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    this.output.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
}

if (!customElements.get('sst-cart-timer')) {
  customElements.define('sst-cart-timer', SstCartTimer);
}
